import { Link, NavLink } from "react-router-dom";
import { LogOut } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { ADMIN_NAV, ADMIN_STOREFRONT_LINK, type AdminNavItem } from "@/constants/adminNav";

interface AdminSidebarProps {
  onNavigate?: () => void;
  onLogout: () => void;
  className?: string;
}

function navLinkClass(isActive: boolean) {
  return [
    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
    isActive
      ? "bg-white/15 text-white"
      : "text-white/70 hover:bg-white/10 hover:text-white",
  ].join(" ");
}

function NavItem({ item, onNavigate }: { item: AdminNavItem; onNavigate?: () => void }) {
  const Icon = item.icon;

  if (item.external) {
    return (
      <a
        href={item.to}
        onClick={onNavigate}
        className={navLinkClass(false)}
        data-testid="admin-nav-storefront"
      >
        <Icon size={18} strokeWidth={1.75} className="shrink-0" />
        {item.label}
      </a>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) => navLinkClass(isActive)}
      data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon size={18} strokeWidth={1.75} className="shrink-0" />
      {item.label}
    </NavLink>
  );
}

export const AdminSidebar = ({ onNavigate, onLogout, className = "" }: AdminSidebarProps) => {
  return (
    <div className={`flex flex-col h-full bg-vg-navy text-white ${className}`}>
      <div className="px-5 py-6 border-b border-white/10">
        <Link to="/admin" onClick={onNavigate} className="inline-flex items-center gap-3">
          <SiteLogo className="h-8 w-auto brightness-0 invert" />
        </Link>
        <p className="mt-3 text-xs font-mono-acc uppercase tracking-[0.2em] text-white/50">
          Admin dashboard
        </p>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {ADMIN_NAV.map((item) => (
          <NavItem key={item.to} item={item} onNavigate={onNavigate} />
        ))}

        <div className="my-4 border-t border-white/10" />

        <NavItem item={ADMIN_STOREFRONT_LINK} onNavigate={onNavigate} />
      </nav>

      <div className="px-3 py-4 border-t border-white/10">
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            onLogout();
          }}
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
          data-testid="admin-logout"
        >
          <LogOut size={18} strokeWidth={1.75} className="shrink-0" />
          Log out
        </button>
      </div>
    </div>
  );
};

export default AdminSidebar;
